<template>
  <div class="tags-selector">
    <div class="tags-selector__icon">
      <BaseSvg name="ico-bookmark" width="0.8rem" height="1.1rem" />
    </div>

    <TagEditMenu
      v-for="tag in value"
      :key="tag.id"
      :colors-entries-map="colorsEntriesMap"
      :tag="tag"
      @rename="updateTagValue"
      @delete="deleteTag"
      @update-color="setTagColor"
    >
      <template v-slot:activator="{ on }">
        <div
          class="tags-selector__tag"
          :style="{ ...getTagColors(tag.color) }"
          v-on="on"
        >
          {{ tag.name }}
        </div>
      </template>
    </TagEditMenu>

    <BaseMenu
      fixed-on-page
      use-close-btn
      :card-style="{ maxWidth: '28rem', minWidth: '28rem', transform: 'translate(-20%, -20%)' }"
      @open="loadGlobalTags"
    >
      <template v-slot:activator="{ on, active }">
        <button
          class="base-btn tags-selector__add-btn"
          v-on="on"
        >
          {{ $tVacancy('add_tag') }}
        </button>
      </template>

      <div>
        <div class="input__label">{{ $tVacancy('find_or_create_tag') }}</div>
        <div class="tags-selector__search-and-add">
          <BaseInput
            v-model="editForm.value"
            @input="onSearchTag"
          />
          <button
            class="base-btn base-btn--outline-dark base-btn--x-small"
            :disabled="!canCreateTag"
            @click="createTag"
          >
            {{ $tVacancy('create') }}
          </button>
        </div>
      </div>

      <div class="mt-5">
        <div>{{ $tVacancy('select_tag') }}:</div>

        <div class="tags-selector__select-tags-list-wrap relative">
          <BasePreloader v-if="loading.allTags" />

          <div v-else class="tags-selector__select-tags-list">
            <div
              v-for="tag in filteredTags"
              :key="tag.id"
              class="tags-selector__select-tags-list-item"
              :class="{ _selected: tag.selected }"
              @click="onFilteredTagClick(tag)"
            >
              <div class="d-flex">
                <div>{{ tag.name }}</div>
                <div v-if="tag.selected" class="ml-2">
                  <BaseSvg name="ico-check" width="1rem" height="0.8rem" fill="#16C15BFF" />
                </div>
              </div>

              <TagEditMenu
                :colors-entries-map="colorsEntriesMap"
                :tag="tag"
                fixed-on-page
                @rename="renameGlobalTag"
                @delete="deleteGlobalTag"
                @update-color="setGlobalTagColor"
              >
                <template v-slot:activator="{ on, active }">
                  <div
                    class="tags-selector__select-tags-list-item-menu-dots"
                    :class="{ 'd-flex': active }"
                    v-on="on"
                    @click.stop=""
                  >
                    <BaseSvg name="ico-dots-2" width="1.7rem" height="0.3rem" />
                  </div>
                </template>
              </TagEditMenu>
            </div>
          </div>
        </div>
      </div>
    </BaseMenu>
  </div>
</template>

<script>
import {cloneDeep, merge} from 'lodash';
import TagEditMenu from './TagEditMenu';
import {http} from '../../axios';

let inputTimeout = null;
let globalTagInputTimeout = null;
let searchInputTimeout = null;

export const Color = {
  Green: 'GREEN',
  Blue: 'BLUE',
  Red: 'RED',
  Purple: 'PURPLE',
  Yellow: 'YELLOW',
  Gray: 'GRAY',
};

const colorsEntriesMap = new Map([
  [Color.Green, {
    color: '#6c8f63',
    backgroundColor: '#edf5e4',
  }],
  [Color.Blue, {
    color: '#0074ed',
    backgroundColor: '#f1f6fc',
  }],
  [Color.Red, {
    color: '#ce7171',
    backgroundColor: '#f8efef',
  }],
  [Color.Purple, {
    color: '#9378b1',
    backgroundColor: '#efe8f6',
  }],
  [Color.Yellow, {
    color: '#9a8f1c',
    backgroundColor: '#f2eeb4',
  }],
  [Color.Gray, {
    color: '#67717f',
    backgroundColor: '#f1f6fc',
  }],
]);

export default {
  name: 'TagsSelector',
  components: {
    TagEditMenu,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    sentResumeId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      colorsEntriesMap,

      loading: {
        allTags: false,
      },

      editForm: {
        value: '',
      },

      allTags: [],
      filteredTags: [],
    };
  },
  computed: {
    canCreateTag() {
      return this.editForm.value && !this.value.some(t => t.value === this.editForm.value);
    },
  },
  watch: {
    allTags() {
      this.filteredTags = this.getFilteredTags();
    },
  },
  methods: {
    async loadGlobalTags() {
      this.loading.allTags = true;

      const { data } = await http.get('/api/cabinet/employer/my-responses/get-tags-list', {
        params: {
          sent_resume_id: this.sentResumeId,
        },
      })
        .catch(() => {})
        .finally(() => this.loading.allTags = false);

      this.allTags = data.data || [];
    },
    renameGlobalTag({ id, color }, value) {
      const newTag = { id, color, name: value };
      this.updateGlobalTagById(newTag);
    },
    setGlobalTagColor({ id, name }, color) {
      const newTag = { id, color, name };
      this.updateGlobalTagById(newTag);
    },
    async deleteGlobalTag({ id }) {
      const { data } = await http.post('/api/cabinet/employer/my-responses/remove-tag', {
        sent_resume_id: this.sentResumeId,
        id,
      }).catch(() => {});
      if (!data?.data) return;

      const idx = this.allTags.findIndex(t => t.id === id);
      this.allTags.splice(idx, 1);
    },
    updateGlobalTagById(newTag) {
      clearTimeout(globalTagInputTimeout);
      globalTagInputTimeout = setTimeout(async () => {
        const { data } = await http.post('/api/cabinet/employer/my-responses/update-tag', newTag).catch(() => {});
        if (!data?.data) return;

        const idx = this.allTags.findIndex(t => t.id === newTag.id);
        const tag = this.allTags[idx];

        const updatedTag = merge(tag, newTag);
        this.allTags.splice(idx, 1, updatedTag);

        const localTagIdx = this.value.findIndex(t => t.id === newTag.id);
        if (localTagIdx !== -1) {
          this.value.splice(localTagIdx, 1, updatedTag);
        }
      }, 500);
    },

    getFilteredTags() {
      if (!this.editForm.value) return this.allTags;
      return this.allTags.filter(t => (
        (t.name || '').toLowerCase().includes(this.editForm.value.toLowerCase())
      ));
    },
    onSearchTag() {
      clearTimeout(searchInputTimeout);
      searchInputTimeout = setTimeout(() => {
        this.filteredTags = this.getFilteredTags();
      }, 500);
    },
    onFilteredTagClick(tag) {
      const { selected, name, color } = tag;
      if (selected) return;
      this.$emit('create-tag', { name, color });
      tag.selected = true;
    },
    transformAndUpdateTag(tag) {
      const tags = cloneDeep(this.value);
      const idx = tags.findIndex(t => t.id === tag.id);

      return {
        transform: (callback) => {
          callback(tags[idx], idx, tags);
          this.$emit('input', tags);
        },
      };
    },
    getTagColors(colorValue) {
      return colorsEntriesMap.get(colorValue);
    },
    createTag() {
      const onSuccess = (tag) => {
        this.editForm.value = '';
        this.filteredTags = this.getFilteredTags();
        if (tag) this.allTags.unshift(tag);
      };
      this.$emit('create-tag', { name: this.editForm.value, color: Color.Green }, onSuccess);
    },
    updateTagValue(tag, value) {
      if (!value) return;

      clearTimeout(inputTimeout);
      inputTimeout = setTimeout(() => {
        this.transformAndUpdateTag(tag)
          .transform((tag) => { tag.name = value.trim() });
      }, 800);
    },
    setTagColor(tag, colorKey) {
      this.transformAndUpdateTag(tag)
        .transform((tag) => { tag.color = colorKey });
    },
    deleteTag(tag) {
      this.transformAndUpdateTag(tag)
        .transform((tag, idx, tags) => { tags.splice(idx, 1) });
    },
  },
};
</script>
